import { RefObject, useCallback, useEffect, useRef } from "react";

/**
 * 가로 스크롤이 필요한 컴포넌트에서 세로 스크롤을 막는 Hook
 *
 * @param ref - Slider 등 가로 스크롤이 필요한 컴포넌트의 ref
 * @param dragThreshold - 좌우 스크롤이 시작되는 최소 이동 거리
 */
export function usePreventVerticalScroll<T extends HTMLElement>(
  ref: RefObject<T>,
  dragThreshold = 5,
) {
  const firstClientX = useRef<number>(0);
  const clientX = useRef<number>(0);

  const touchStart = useCallback((e: TouchEvent) => {
    firstClientX.current = e.touches[0].clientX;
  }, []);

  const preventTouch = useCallback(
    (e: TouchEvent) => {
      clientX.current = e.touches[0].clientX - firstClientX.current;

      // NOTE: 좌우 스크롤이 시작된 경우 상하 스크롤 동작하지 않도록 막음
      if (Math.abs(clientX.current) > dragThreshold) {
        e.preventDefault();
        e.returnValue = false;
        return false;
      }

      return true;
    },
    [dragThreshold],
  );

  useEffect(() => {
    const current = ref.current;

    if (current) {
      current.addEventListener("touchstart", touchStart);
      current.addEventListener("touchmove", preventTouch, { passive: false });
    }

    return () => {
      if (current) {
        current.removeEventListener("touchstart", touchStart);
        current.removeEventListener("touchmove", preventTouch);
      }
    };
  }, [preventTouch, ref, touchStart]);
}
